import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { useAdminContext } from '../../../contexts/AdminContext';

// Helper: render the compact form, used for both Add and Edit modals
const CompactUseCaseForm = ({ value, onChange, categories, domains, difficulties, impacts }) => {
  // Convert tools array <-> comma-separated string for the input
  const toolsAsString = Array.isArray(value.tools) ? value.tools.join(', ') : (value.tools || '');

  return (
    <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-1">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Titel *</label>
        <Input
          data-testid="usecase-form-title"
          value={value.title || ''}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
          className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
          placeholder="Bijv. AI-gestuurde CV screening"
        />
      </div>

      {/* Short Description */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Korte beschrijving *</label>
        <textarea
          data-testid="usecase-form-description"
          value={value.shortDescription || ''}
          onChange={(e) => onChange({ ...value, shortDescription: e.target.value })}
          className="w-full px-3 py-2 bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md text-slate-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="In één of twee zinnen wat deze use case oplost en hoe AI helpt."
        />
      </div>

      {/* Category + Secondary Category (dual badge) */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Categorie *</label>
          <Select value={value.category || 'quick-wins'} onValueChange={(v) => onChange({ ...value, category: v })}>
            <SelectTrigger data-testid="usecase-form-category" className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.filter(c => c.value !== 'all').map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Tweede categorie <span className="text-slate-400">(optioneel)</span>
          </label>
          <Select
            value={value.secondaryCategory || 'none'}
            onValueChange={(v) => onChange({ ...value, secondaryCategory: v === 'none' ? '' : v })}
          >
            <SelectTrigger data-testid="usecase-form-secondary-category" className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white">
              <SelectValue placeholder="Geen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Geen</SelectItem>
              {categories.filter(c => c.value !== 'all' && c.value !== value.category).map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Domain + Difficulty */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Domein *</label>
          <Select value={value.domain || 'recruitment'} onValueChange={(v) => onChange({ ...value, domain: v })}>
            <SelectTrigger data-testid="usecase-form-domain" className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {domains.filter(d => d.value !== 'all').map((d) => (
                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Moeilijkheid *</label>
          <Select value={value.difficulty || 'low'} onValueChange={(v) => onChange({ ...value, difficulty: v })}>
            <SelectTrigger data-testid="usecase-form-difficulty" className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {difficulties.filter(d => d.value !== 'all').map((d) => (
                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Impact + Hours Saved */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Impact *</label>
          <Select value={value.impact || 'medium'} onValueChange={(v) => onChange({ ...value, impact: v })}>
            <SelectTrigger data-testid="usecase-form-impact" className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(impacts || []).filter(i => i.value !== 'all').map((i) => (
                <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Uren bespaard per maand <span className="text-slate-400">(ROI)</span>
          </label>
          <Input
            data-testid="usecase-form-hours-saved"
            type="number"
            min={0}
            value={value.hoursSaved ?? 0}
            onChange={(e) => onChange({ ...value, hoursSaved: e.target.value })}
            className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
            placeholder="bv. 40"
          />
        </div>
      </div>

      {/* Tools */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Tools <span className="text-slate-400">(komma-gescheiden)</span>
        </label>
        <Input
          data-testid="usecase-form-tools"
          value={toolsAsString}
          onChange={(e) => onChange({
            ...value,
            tools: e.target.value.split(',').map(t => t.trim()).filter(Boolean),
          })}
          className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
          placeholder="bv. HireVue, LinkedIn Recruiter, Personio"
        />
      </div>
    </div>
  );
};

const UseCaseModals = () => {
  const {
    categories,
    difficulties,
    domains,
    impacts,
    editingUseCase,
    handleAddUseCase,
    handleUpdateUseCase,
    newUseCase,
    setEditingUseCase,
    setNewUseCase,
    setShowAddModal,
    showAddModal,
  } = useAdminContext();

  const isValid = (v) => v && v.title?.trim() && v.shortDescription?.trim();

  return (
    <>
      {/* Add Use Case Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent
          data-testid="add-usecase-modal"
          className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white max-w-2xl"
        >
          <DialogHeader>
            <DialogTitle>Nieuwe Use Case</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              Voeg een nieuwe HR-AI use case toe — direct zichtbaar op de site.
            </DialogDescription>
          </DialogHeader>

          <CompactUseCaseForm
            value={newUseCase}
            onChange={setNewUseCase}
            categories={categories || []}
            domains={domains || []}
            difficulties={difficulties || []}
            impacts={impacts || []}
          />

          <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-700 mt-4">
            <Button
              data-testid="add-usecase-cancel-btn"
              variant="outline"
              onClick={() => setShowAddModal(false)}
              className="flex-1 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
            >
              Annuleren
            </Button>
            <Button
              data-testid="add-usecase-submit-btn"
              onClick={handleAddUseCase}
              disabled={!isValid(newUseCase)}
              className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Use Case Aanmaken
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Use Case Modal */}
      <Dialog open={!!editingUseCase} onOpenChange={() => setEditingUseCase(null)}>
        <DialogContent
          data-testid="edit-usecase-modal"
          className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white max-w-2xl"
        >
          <DialogHeader>
            <DialogTitle>Use Case Bewerken</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              Werk de gegevens van deze use case bij.
            </DialogDescription>
          </DialogHeader>

          {editingUseCase && (
            <>
              <CompactUseCaseForm
                value={{
                  ...editingUseCase,
                  shortDescription: editingUseCase.shortDescription || editingUseCase.description || '',
                }}
                onChange={setEditingUseCase}
                categories={categories || []}
                domains={domains || []}
                difficulties={difficulties || []}
                impacts={impacts || []}
              />
              <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-700 mt-4">
                <Button
                  data-testid="edit-usecase-cancel-btn"
                  variant="outline"
                  onClick={() => setEditingUseCase(null)}
                  className="flex-1 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
                >
                  Annuleren
                </Button>
                <Button
                  data-testid="edit-usecase-submit-btn"
                  onClick={handleUpdateUseCase}
                  disabled={!isValid(editingUseCase)}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Wijzigingen Opslaan
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UseCaseModals;
